import { loadJSZip } from "@/utils/lazyLibs";
import axiosInstance from "@/lib/axios";
import type { AttachmentFolderDTO } from "@/hooks/api/useAttachmentFolders";

export type DocumentsZipLayout = "folder_tree" | "flat_lead_folder";

function sanitizePathSegment(s: string): string {
  const t = s.replace(/[/\\?*:|"<>]/g, "_").replace(/\s+/g, " ").trim();
  return t || "unnamed";
}

function sanitizeFileName(s: string): string {
  const t = String(s).replace(/[/\\]/g, "_").trim();
  return t || "file";
}

/**
 * Common image / office MIME types we expect for attachments. Anything not
 * in the map falls through to `null` and the caller leaves the file with no
 * extension (still better than guessing wrong).
 */
const MIME_TO_EXT: Record<string, string> = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/heic": "heic",
  "image/svg+xml": "svg",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "text/plain": "txt",
  "text/csv": "csv",
  "application/zip": "zip",
  "application/x-zip-compressed": "zip",
  "application/json": "json",
};

function hasExtension(name: string): boolean {
  return /\.[A-Za-z0-9]{1,8}$/.test(name);
}

function extFromS3Key(s3Key?: string | null): string | null {
  if (!s3Key) return null;
  const m = String(s3Key).match(/\.([A-Za-z0-9]{1,8})$/);
  return m ? m[1].toLowerCase() : null;
}

function extFromType(type?: string | null): string | null {
  if (!type) return null;
  const t = String(type).trim().toLowerCase();
  if (!t) return null;
  if (t.includes("/")) return MIME_TO_EXT[t] ?? null;
  // Already a bare extension (e.g. "pdf", "docx")
  return /^[a-z0-9]{1,8}$/.test(t) ? t : null;
}

/**
 * Resolve the on-disk filename for a downloaded attachment.
 *
 * The `Documents` table can have `fileName` stored either with or without
 * an extension — legacy / migrated rows and user-renamed docs often dropped
 * the extension, which broke macOS opening (Finder shows a generic "?" icon
 * and refuses to launch a handler).
 *
 * Resolution order:
 *   1. If `fileName` already ends in an extension, use it as-is (basename).
 *   2. Otherwise pick a base from `fileName` / `name` / `id` fallback and
 *      append the extension recovered from `s3Key` (most reliable — keys
 *      look like `<uuid>.pdf`) or `type` (MIME or short ext).
 *   3. If no extension can be recovered, return the base unchanged — the
 *      browser will save it as a typeless file, same as today.
 */
export function resolveDownloadFilename(doc: {
  id?: number | string;
  fileName?: string | null;
  name?: string | null;
  type?: string | null;
  s3Key?: string | null;
}): string {
  const basename = (s: string) => s.split("/").pop()!.trim();

  const fromFileName = doc.fileName ? basename(String(doc.fileName)) : "";

  if (fromFileName && hasExtension(fromFileName)) {
    return fromFileName;
  }

  const base =
    fromFileName ||
    (doc.name && String(doc.name).trim()) ||
    `document-${doc.id ?? "file"}`;

  const ext = extFromS3Key(doc.s3Key) || extFromType(doc.type);
  return ext ? `${base}.${ext}` : base;
}

function uniqueZipPath(used: Set<string>, dir: string, fileName: string): string {
  const base = sanitizeFileName(fileName);
  let path = dir ? `${dir}/${base}` : base;
  if (!used.has(path)) {
    used.add(path);
    return path;
  }
  const dot = base.lastIndexOf(".");
  const stem = dot === -1 ? base : base.slice(0, dot);
  const ext = dot === -1 ? "" : base.slice(dot);
  let n = 2;
  while (true) {
    const inner = `${stem} (${n})${ext}`;
    path = dir ? `${dir}/${inner}` : inner;
    if (!used.has(path)) {
      used.add(path);
      return path;
    }
    n++;
  }
}

function folderRelativePath(
  folderId: number | null | undefined,
  folderById: Map<number, AttachmentFolderDTO>
): string {
  if (folderId == null) return "";
  const parts: string[] = [];
  let cur: number | null | undefined = folderId;
  const guard = new Set<number>();
  while (cur != null && !guard.has(cur)) {
    guard.add(cur);
    const f = folderById.get(cur);
    if (!f) break;
    parts.unshift(sanitizePathSegment(f.name));
    cur = f.parentId ?? null;
  }
  return parts.join("/");
}

// Stream via our own API origin instead of DO Spaces directly: Spaces has no
// CORS policy for our tenant hosts, so a cross-origin fetch of the public
// fileUrl fails with "Failed to fetch". The server proxy is tenant-scoped.
async function fetchFileBlob(docId: number): Promise<Blob> {
  const res = await axiosInstance.get(`/core/doc/${docId}/download`, {
    responseType: "blob",
  });
  return res.data as Blob;
}

export async function buildDocumentsZip(options: {
  documents: any[];
  folders: AttachmentFolderDTO[];
  layout: DocumentsZipLayout;
  leadName: string;
}): Promise<{ blob: Blob; fileName: string }> {
  const { documents, folders, layout, leadName } = options;
  const withUrl = documents.filter(
    (d) => d?.id != null && d?.fileUrl && String(d.fileUrl).trim()
  );
  if (withUrl.length === 0) {
    throw new Error("No files with download links");
  }

  const folderById = new Map(folders.map((f) => [f.id, f]));
  const usedPaths = new Set<string>();
  const zip = new (await loadJSZip())();
  const leadRoot = sanitizePathSegment(leadName || "Lead");

  for (const doc of withUrl) {
    const raw = resolveDownloadFilename(doc);
    const blob = await fetchFileBlob(Number(doc.id));

    let zipPath: string;
    if (layout === "flat_lead_folder") {
      zipPath = uniqueZipPath(usedPaths, leadRoot, raw);
    } else {
      const dir = folderRelativePath(doc.folderId ?? null, folderById);
      const nameForZip = uniqueZipPath(usedPaths, dir, raw);
      zipPath = nameForZip;
    }

    zip.file(zipPath, blob);
  }

  const blob = await zip.generateAsync({ type: "blob" });
  const fileName = `${leadRoot}-documents.zip`;
  return { blob, fileName };
}

export function triggerBlobDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
